import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { SPRING_TRANSITION } from "../shared/constants";
import {
	type FloatingToolbarProps,
	HorizontalFloatingToolbar,
	VerticalFloatingToolbar,
} from "./floating-toolbar";
import { getToolbarColors } from "./toolbar-colors";
import {
	FabBaselineTokens,
	FloatingToolbarTokens,
	ToolbarToFabGap,
} from "./toolbar-tokens";

export interface FloatingToolbarWithFabProps extends FloatingToolbarProps {
	/** FAB element (use FAB component or custom node) */
	floatingActionButton: React.ReactNode;
	/** FAB position for horizontal: 'start' | 'end'. For vertical: 'top' | 'bottom' */
	fabPosition?: "start" | "end" | "top" | "bottom";
	/** Animation duration override */
	animationDuration?: number;
	/** Start content (explicitly declared to resolve type issues) */
	startContent?: React.ReactNode;
	/** End content (explicitly declared to resolve type issues) */
	endContent?: React.ReactNode;
	/**
	 * FAB size embedded in the floating toolbar.
	 * - `"sm"` – standard FAB: 56×56dp (MD3 default).
	 * - `"md"` – medium FAB: 80×80dp (`FabBaselineTokens.MediumContainerHeight`).
	 * Corresponds to MD3 Toolbar Floating FAB token sizes.
	 * @default "sm"
	 */
	fabSize?: "sm" | "md";
}

const FloatingToolbarWithFabBase = React.forwardRef<
	HTMLDivElement,
	FloatingToolbarWithFabProps
>(
	(
		{
			expanded,
			orientation = "horizontal",
			variant = "standard",
			colors: customColors,
			floatingActionButton,
			fabPosition = orientation === "horizontal" ? "end" : "bottom",
			animationDuration = 0.3,
			scrollBehavior,
			fabSize = "sm",
			className,
			style,
			...props
		},
		ref,
	) => {
		const colors = getToolbarColors(variant, customColors);

		const isHorizontal = orientation === "horizontal";

		const effectiveExpanded =
			expanded && (!scrollBehavior || scrollBehavior.isExpanded);

		const isFabBefore = fabPosition === "start" || fabPosition === "top";

		const resolvedFabSize =
			fabSize === "md"
				? FabBaselineTokens.MediumContainerHeight
				: FabBaselineTokens.ContainerHeight;

		const transitionSpec = {
			...SPRING_TRANSITION,
			duration: animationDuration,
		};

		const SHADOW_PADDING = 24;

		const fabContainerNode = (
			<m.div
				layout
				key="fab-container"
				style={{
					width: `${resolvedFabSize}px`,
					height: `${resolvedFabSize}px`,
				}}
				className={cn(
					"flex shrink-0 items-center justify-center relative z-10",
					"*:w-full *:h-full",
				)}
			>
				{floatingActionButton}
			</m.div>
		);

		return (
			<div ref={ref} style={style}>
				<m.div
					layout
					initial={false}
					animate={{
						gap: effectiveExpanded ? `${ToolbarToFabGap}px` : "0px",
					}}
					transition={transitionSpec}
					className={cn(
						"flex items-center justify-center pointer-events-auto",
						isHorizontal
							? "flex-row h-(--toolbar-size)"
							: "flex-col w-(--toolbar-size) h-fit",
						className,
					)}
					style={
						{
							"--fab-size": `${resolvedFabSize}px`,
							"--toolbar-size": `${FloatingToolbarTokens.ContainerHeight}px`,
						} as React.CSSProperties
					}
				>
					{isFabBefore && fabContainerNode}

					<AnimatePresence initial={false}>
						{effectiveExpanded && (
							<m.div
								key="toolbar-content-wrapper"
								initial={{
									opacity: 0,
									scale: 0.9,
									width: isHorizontal ? 0 : "auto",
									height: !isHorizontal ? 0 : "auto",
								}}
								animate={{
									opacity: 1,
									scale: 1,
									width: "auto",
									height: "auto",
								}}
								exit={{
									opacity: 0,
									scale: 0.9,
									width: isHorizontal ? 0 : "auto",
									height: !isHorizontal ? 0 : "auto",
								}}
								transition={transitionSpec}
								style={{
									padding: SHADOW_PADDING,
									margin: -SHADOW_PADDING,
								}}
								className="flex items-center shrink-0 min-w-0 min-h-0 overflow-hidden relative z-0"
							>
								{isHorizontal ? (
									<HorizontalFloatingToolbar
										expanded={expanded}
										colors={colors}
										disableScrollTranslation
										disableLayoutAnimation
										className={className ? cn("h-full", className) : undefined}
										{...props}
									/>
								) : (
									<VerticalFloatingToolbar
										expanded={expanded}
										colors={colors}
										disableScrollTranslation
										disableLayoutAnimation
										className={className ? cn("w-full", className) : undefined}
										{...props}
									/>
								)}
							</m.div>
						)}
					</AnimatePresence>

					{!isFabBefore && fabContainerNode}
				</m.div>
			</div>
		);
	},
);
FloatingToolbarWithFabBase.displayName = "FloatingToolbarWithFabBase";

/**
 * A horizontal floating toolbar paired with a FAB.
 */
export const HorizontalFloatingToolbarWithFab = React.forwardRef<
	HTMLDivElement,
	FloatingToolbarWithFabProps
>((props, ref) => (
	<FloatingToolbarWithFabBase ref={ref} orientation="horizontal" {...props} />
));
HorizontalFloatingToolbarWithFab.displayName =
	"HorizontalFloatingToolbarWithFab";

/**
 * A vertical floating toolbar paired with a FAB.
 */
export const VerticalFloatingToolbarWithFab = React.forwardRef<
	HTMLDivElement,
	FloatingToolbarWithFabProps
>((props, ref) => (
	<FloatingToolbarWithFabBase ref={ref} orientation="vertical" {...props} />
));
VerticalFloatingToolbarWithFab.displayName = "VerticalFloatingToolbarWithFab";
